import {
  computed,
  watch,
  onMounted,
  useRoute,
  useRouter,
  nextTick,
} from "@nuxtjs/composition-api";

/**
 * Composable for hash-based URL parameters.
 * Hash fragments are not sent to the server, so:
 * - SSR always renders with default values (page 1, no keyword)
 * - Client-side mounted() loads the correct values based on hash
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onParamsChange - Callback when hash params change (page, keyword)
 * @param {Object} options.scrollToRef - Optional ref to scroll to after params change
 * @returns {Object} Hash params utilities
 */
export default function useHashParams(options = {}) {
  const { onParamsChange, scrollToRef } = options;
  const route = useRoute();
  const router = useRouter();

  /**
   * Parse hash params from route.value.hash
   * Handles format: #page=2&keyword=test
   */
  const hashParams = computed(() => {
    const hash = route.value.hash || "";
    const params = {};

    // Remove leading # if present
    const hashContent = hash.startsWith("#") ? hash.slice(1) : hash;
    if (!hashContent) return params;

    // Parse key=value pairs
    const pairs = hashContent.split("&");
    for (const pair of pairs) {
      const [key, value] = pair.split("=");
      if (key && value !== undefined) {
        params[key] = decodeURIComponent(value);
      }
    }

    return params;
  });

  /**
   * Current page number from hash (defaults to 1)
   */
  const page = computed(() => {
    const pageValue = hashParams.value.page;
    return pageValue ? parseInt(pageValue, 10) : 1;
  });

  /**
   * Current keyword from hash (defaults to empty string)
   */
  const keyword = computed(() => hashParams.value.keyword || "");

  /**
   * Update hash params using Vue Router
   * @param {Object} params - New params to set
   * @param {boolean} replace - Use router.replace instead of router.push
   */
  function setHashParams(params, replace = false) {
    if (typeof window === "undefined") return;

    const hashParts = [];
    if (params.page && params.page > 1) {
      hashParts.push(`page=${params.page}`);
    }
    if (params.keyword) {
      hashParts.push(`keyword=${encodeURIComponent(params.keyword)}`);
    }

    const newHash = hashParts.length ? `#${hashParts.join("&")}` : "";
    const newPath = `${route.value.path}${newHash}`;

    // Use Vue Router to navigate - this updates $route reactively
    if (replace) {
      router.replace(newPath);
    } else {
      router.push(newPath);
    }
  }

  /**
   * Set page number in hash
   * @param {number} newPage - Page number to set
   */
  function setPage(newPage) {
    setHashParams({ page: newPage, keyword: keyword.value });
  }

  /**
   * Set keyword in hash (resets page to 1)
   * @param {string} newKeyword - Keyword to set
   */
  function setKeyword(newKeyword) {
    setHashParams({ page: 1, keyword: newKeyword });
  }

  /**
   * Generate URL path with hash params
   * @param {number} targetPage - Target page number
   * @param {string} targetKeyword - Target keyword (defaults to current)
   * @returns {string} URL with hash params
   */
  function getHashPath(targetPage, targetKeyword = keyword.value) {
    const hashParts = [];
    if (targetPage > 1) {
      hashParts.push(`page=${targetPage}`);
    }
    if (targetKeyword) {
      hashParts.push(`keyword=${encodeURIComponent(targetKeyword)}`);
    }
    const hashStr = hashParts.length ? `#${hashParts.join("&")}` : "";
    return `${route.value.path}${hashStr}`;
  }

  // Watch hash changes and call callback
  watch(
    () => route.value.hash,
    () => {
      if (onParamsChange) {
        onParamsChange(page.value, keyword.value);
      }
      // Scroll to ref if provided
      if (scrollToRef?.value) {
        nextTick(() => {
          scrollToRef.value.scrollIntoView({ behavior: "smooth" });
        });
      }
    }
  );

  // On mount, load correct values if hash has non-default params
  onMounted(() => {
    if (page.value > 1 || keyword.value) {
      if (onParamsChange) {
        onParamsChange(page.value, keyword.value);
      }
    }
  });

  return {
    page,
    keyword,
    hashParams,
    setPage,
    setKeyword,
    setHashParams,
    getHashPath,
  };
}

/**
 * Options API mixin for hash-based URL parameters.
 * Use this in components that need hash params with Options API.
 *
 * Usage:
 * 1. Import and add to mixins: mixins: [hashParamsMixin]
 * 2. Implement loadPageData(page, keyword) method
 * 3. Optionally set hashParamsScrollRef to a ref name for scroll-to behavior
 */
export const hashParamsMixin = {
  data() {
    return {
      hashParamsScrollRef: null, // Override in component to set scroll target ref name
    };
  },

  computed: {
    /**
     * Parse hash params from route hash
     */
    hashParams() {
      const hash = this.$route.hash || "";
      const params = {};

      const hashContent = hash.startsWith("#") ? hash.slice(1) : hash;
      if (!hashContent) return params;

      const pairs = hashContent.split("&");
      for (const pair of pairs) {
        const [key, value] = pair.split("=");
        if (key && value !== undefined) {
          params[key] = decodeURIComponent(value);
        }
      }

      return params;
    },

    /**
     * Current page from hash
     */
    hashPage() {
      const pageValue = this.hashParams.page;
      return pageValue ? parseInt(pageValue, 10) : 1;
    },

    /**
     * Current keyword from hash
     */
    hashKeyword() {
      return this.hashParams.keyword || "";
    },
  },

  watch: {
    /**
     * Watch route hash changes
     */
    "$route.hash": {
      handler() {
        this.onHashParamsChange();
      },
    },
  },

  mounted() {
    // Load correct page if hash has non-default params
    if (this.hashPage > 1 || this.hashKeyword) {
      this.onHashParamsChange();
    }
  },

  methods: {
    /**
     * Handle hash params change - override loadPageData in component
     */
    onHashParamsChange() {
      if (typeof this.loadPageData === "function") {
        this.loadPageData(this.hashPage, this.hashKeyword);
      }

      // Scroll to ref if configured
      if (this.hashParamsScrollRef && this.$refs[this.hashParamsScrollRef]) {
        this.$nextTick(() => {
          this.$refs[this.hashParamsScrollRef].scrollIntoView({
            behavior: "smooth",
          });
        });
      }
    },

    /**
     * Set hash params using Vue Router
     */
    setHashParams(params, replace = false) {
      if (typeof window === "undefined") return;

      const hashParts = [];
      if (params.page && params.page > 1) {
        hashParts.push(`page=${params.page}`);
      }
      if (params.keyword) {
        hashParts.push(`keyword=${encodeURIComponent(params.keyword)}`);
      }

      const newHash = hashParts.length ? `#${hashParts.join("&")}` : "";
      const newPath = `${this.$route.path}${newHash}`;

      // Use Vue Router to navigate - this updates $route reactively
      if (replace) {
        this.$router.replace(newPath);
      } else {
        this.$router.push(newPath);
      }
    },

    /**
     * Set page in hash
     */
    setHashPage(newPage) {
      this.setHashParams({ page: newPage, keyword: this.hashKeyword });
    },

    /**
     * Set keyword in hash (resets page to 1)
     */
    setHashKeyword(newKeyword) {
      this.setHashParams({ page: 1, keyword: newKeyword });
    },

    /**
     * Generate URL path with hash params
     */
    getHashPath(targetPage, targetKeyword = this.hashKeyword) {
      const hashParts = [];
      if (targetPage > 1) {
        hashParts.push(`page=${targetPage}`);
      }
      if (targetKeyword) {
        hashParts.push(`keyword=${encodeURIComponent(targetKeyword)}`);
      }
      const hashStr = hashParts.length ? `#${hashParts.join("&")}` : "";
      return `${this.$route.path}${hashStr}`;
    },
  },
};
